import React, { useEffect, useState, useRef, useCallback } from 'react'
import { css } from '@emotion/core'
import { Image } from '@fs/zion-ui'

const carouselCss = css`
  position: relative;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  .carousel-images {
    position: relative;
    height: 100%;
  }

  .carousel-img {
    position: absolute;
    inset: 0;

    opacity: 0;
    transition: opacity 1.5s ease;
  }

  .carousel-img.active {
    opacity: 1;
    z-index: 1;
  }

  .carousel-img.full-height {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .carousel-img.full-height img {
    height: 100%;
    width: auto;
  }
`

export default function ImageSlideshow({
  images,
  interval = 12000,
  maxHeight,
  bgImage,
  bgImagePosition,
  fullHeight = false, // Makes images take full height of container, helps when using images from Figma designs
}) {
  const [index, setIndex] = useState(0)
  const [loadedImages, setLoadedImages] = useState(new Set([0])) // Start with first image loaded
  const timerRef = useRef(null)

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length])

  // Preload next image when index changes
  useEffect(() => {
    const nextIndex = (index + 1) % images.length
    if (!loadedImages.has(nextIndex)) {
      setLoadedImages((prev) => new Set([...prev, nextIndex]))
    }
  }, [index, images.length, loadedImages])

  // Auto advance logic
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      next()
    }, interval)
    return () => clearTimeout(timerRef.current)
  }, [index, interval, next])

  return (
    <div
      data-testid="image-slideshow"
      // Decorative: no information or controls, so it is hidden from assistive tech
      aria-hidden="true"
      css={carouselCss}
      style={{ backgroundImage: `url(${bgImage})`, backgroundPosition: bgImagePosition }}
    >
      <div className="carousel-images">
        {images.map((img, i) => (
          <div
            className={`carousel-img ${i === index ? 'active' : ''} ${fullHeight ? 'full-height' : ''}`}
            key={img.key}
          >
            {/* Only render image if it should be loaded (current or preloaded) */}
            {loadedImages.has(i) && (
              <Image noCrop maxHeight={maxHeight} height="100%" positionY="bottom" src={img.src} alt={img.alt || ''} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
